import { Spinner, TextField } from "@king-ict/eupisi-ui/components";
import { useTranslation } from "react-i18next";

import { QuestionInput } from "./question-input/QuestionInput";
import { FormCard } from "../../../../components";
import { SectionHeader } from "../../../../components/FormControls";
import { TextAreaWithCount } from "../../../../components/Inputs";
import {
  Question,
  DisplayTypeEnum,
} from "../../../../models/Questionnaire/Question";
import { QuestionAnswerDto } from "../../../../models/Questionnaire/QuestionAnswer";
import { useGetQuestionnaireQuestionsQuery } from "../../../../queries/requestQuery";

interface IProps {
  questionAnswers: QuestionAnswerDto[];
  onUpdate: (updatedAnswers: QuestionAnswerDto[]) => void;
  specialData: string | null;
  onSpecialDataUpdate: (updatedSpecialData: string) => void;
}

export const OtherQuestionsAccordion = ({
  questionAnswers,
  onUpdate,
  specialData,
  onSpecialDataUpdate,
}: IProps) => {
  const { t } = useTranslation();
  const { data: questions, isLoading } = useGetQuestionnaireQuestionsQuery();

  if (isLoading) {
    return <Spinner show />;
  }

  const handleAnswerChange = (
    questionId: number,
    answerId: number | null,
    answerText: string | null
  ) => {
    const existingAnswerIndex = questionAnswers.findIndex(
      (answer) => answer.questionId === questionId
    );

    let newAnswers;
    if (existingAnswerIndex >= 0) {
      // Update existing answer
      newAnswers = [...questionAnswers];
      newAnswers[existingAnswerIndex] = {
        ...newAnswers[existingAnswerIndex],
        answerId,
        answerText,
      };
    } else {
      // Add new answer
      const question = questions?.find(
        (q) => q.questionGroupQuestionId === questionId
      );
      newAnswers = [
        ...questionAnswers,
        {
          id: 0, // This will be set by the backend
          questionId,
          question: question?.name || null,
          answerId,
          answer: null,
          answerText,
        },
      ];
    }
    onUpdate(newAnswers);
  };

  return (
    <FormCard className="position-relative">
      <SectionHeader title={t("enrollmentForm.sections.other")} />
      <div className="space-y-4">
        {questions?.map((question: Question) => {
          const currentAnswer = questionAnswers.find(
            (answer) => answer.questionId === question.questionGroupQuestionId
          );
          const value =
            currentAnswer?.answerText || currentAnswer?.answerId || null;

          return (
            <div key={question.questionGroupQuestionId} className="mb-4">
              <QuestionInput
                question={question}
                value={value}
                onChange={(newValue) => {
                  if (
                    question.displayTypeId === DisplayTypeEnum.Select ||
                    question.displayTypeId === DisplayTypeEnum.Radio
                  ) {
                    handleAnswerChange(
                      question.questionGroupQuestionId,
                      newValue as number,
                      null
                    );
                  } else {
                    handleAnswerChange(
                      question.questionGroupQuestionId,
                      null,
                      newValue as string
                    );
                  }
                }}
              />
              {/* This is left because the first question will require upon selecting "ostalo" a text field. 
              Hardcoded until refactor */}
              {question.questionGroupQuestionId === 29 &&
                currentAnswer?.answerId === 38 && (
                  <TextField
                    value={currentAnswer?.answerText || ""}
                    onChange={(e) => {
                      handleAnswerChange(
                        question.questionGroupQuestionId,
                        currentAnswer?.answerId || null,
                        e.target.value
                      );
                    }}
                    className="mt-2"
                    maxLength={254}
                  />
                )}
            </div>
          );
        })}
        <TextAreaWithCount
          value={specialData ?? ""}
          onChange={(value) => {
            onSpecialDataUpdate(value);
          }}
          label={t("enrollmentForm.other.title")}
          ariaLabel={t("enrollmentForm.other.title")}
          rows={4}
          maxLength={254}
        />
      </div>
    </FormCard>
  );
};
